'use client';

import { registrarEvento } from '@/services/googleAnalytics';
import { useEffect, useMemo, useRef, useState } from 'react';

interface UniversalScrollTrackerProps {
  pagina: string;
  debug?: boolean;
}

export function UniversalScrollTracker({
  pagina = '',
  debug = false,
}: UniversalScrollTrackerProps) {
  const thresholds = useMemo(() => [25, 50, 75, 90], []);
  const [scrollSource, setScrollSource] = useState<string>('detecting...');
  const [scrollInfo, setScrollInfo] = useState({
    scrollTop: 0,
    scrollHeight: 0,
    clientHeight: 0,
    scrollableHeight: 0,
    isScrollable: false,
  });
  const triggeredThresholds = useRef<Set<number>>(new Set());
  const hasUserScrolled = useRef(false);
  const detectedSource = useRef<string>('');
  const detectedScrollFunctions = useRef<{
    getScrollTop: () => number;
    getScrollHeight: () => number;
    getClientHeight: () => number;
  }>();

  useEffect(() => {
    const detectScrollSource = () => {
      // Test different scroll sources to find which one is active
      const sources = [
        {
          name: 'body',
          element: document.body,
          getScrollTop: () => document.body.scrollTop,
          getScrollHeight: () => document.body.scrollHeight,
          getClientHeight: () => document.body.clientHeight,
        },
        {
          name: 'documentElement',
          element: document.documentElement,
          getScrollTop: () => document.documentElement.scrollTop,
          getScrollHeight: () => document.documentElement.scrollHeight,
          getClientHeight: () => document.documentElement.clientHeight,
        },
        {
          name: 'window',
          element: window,
          getScrollTop: () => window.pageYOffset || window.scrollY,
          getScrollHeight: () =>
            Math.max(
              document.body.scrollHeight,
              document.documentElement.scrollHeight,
            ),
          getClientHeight: () => window.innerHeight,
        },
      ];

      // Find the source with the largest scrollable height
      let bestSource = sources[0];
      let maxScrollableHeight = 0;

      sources.forEach((source) => {
        try {
          const scrollHeight = source.getScrollHeight();
          const clientHeight = source.getClientHeight();
          const scrollableHeight = scrollHeight - clientHeight;

          if (scrollableHeight > maxScrollableHeight) {
            maxScrollableHeight = scrollableHeight;
            bestSource = source;
          }
        } catch (error) {
          console.log(`Error testing ${source.name}:`, error);
        }
      });

      detectedSource.current = bestSource.name;
      detectedScrollFunctions.current = {
        getScrollTop: bestSource.getScrollTop,
        getScrollHeight: bestSource.getScrollHeight,
        getClientHeight: bestSource.getClientHeight,
      };
      setScrollSource(bestSource.name);

      const scrollTop = bestSource.getScrollTop();
      const scrollHeight = bestSource.getScrollHeight();
      const clientHeight = bestSource.getClientHeight();
      const scrollableHeight = scrollHeight - clientHeight;

      setScrollInfo({
        scrollTop,
        scrollHeight,
        clientHeight,
        scrollableHeight,
        isScrollable: scrollableHeight > 50,
      });

      if (debug) {
        console.log(`🔍 Detected scroll source: ${bestSource.name}`, {
          scrollTop,
          scrollHeight,
          clientHeight,
          scrollableHeight,
          isScrollable: scrollableHeight > 50,
        });
      }

      return bestSource;
    };

    // Detect scroll source with multiple attempts
    detectScrollSource();
    const timer1 = setTimeout(() => {
      detectScrollSource();
    }, 500);
    const timer2 = setTimeout(() => {
      detectScrollSource();
    }, 1000);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, [debug]);

  useEffect(() => {
    if (!scrollInfo.isScrollable) return;

    const handleScroll = () => {
      if (!detectedScrollFunctions.current) return;

      const scrollTop = detectedScrollFunctions.current.getScrollTop();
      const scrollHeight = detectedScrollFunctions.current.getScrollHeight();
      const clientHeight = detectedScrollFunctions.current.getClientHeight();
      const scrollableHeight = scrollHeight - clientHeight;

      // Update scroll info
      setScrollInfo({
        scrollTop,
        scrollHeight,
        clientHeight,
        scrollableHeight,
        isScrollable: scrollableHeight > 50,
      });

      // Mark as user scrolled
      if (!hasUserScrolled.current && scrollTop > 10) {
        hasUserScrolled.current = true;
      }

      if (!hasUserScrolled.current) return;

      // Calculate percentage
      const scrollPercentage =
        scrollableHeight > 0 ? (scrollTop / scrollableHeight) * 100 : 0;

      // Check thresholds
      thresholds.forEach((threshold) => {
        if (
          scrollPercentage >= threshold &&
          !triggeredThresholds.current.has(threshold)
        ) {
          triggeredThresholds.current.add(threshold);

          if (debug) {
            console.log(
              `🎯 UNIVERSAL SCROLL MILESTONE: ${threshold}% reached (${scrollPercentage.toFixed(1)}%) via ${detectedSource.current}`,
            );
          }

          // Send GA event
          registrarEvento('scroll_pagina_' + pagina, {
            event_category: 'engagement',
            event_label: `${threshold}% Universal Scroll`,
            value: threshold,
            scroll_percentage: Math.round(scrollPercentage * 100) / 100,
            scroll_source: detectedSource.current,
            scroll_height: scrollHeight,
            scrollable_height: scrollableHeight,
          });
        }
      });
    };

    // Add listeners to all possible scroll sources
    const addListeners = () => {
      if (detectedSource.current === 'body') {
        document.body.addEventListener('scroll', handleScroll, {
          passive: true,
        });
      } else if (detectedSource.current === 'documentElement') {
        document.documentElement.addEventListener('scroll', handleScroll, {
          passive: true,
        });
      } else {
        window.addEventListener('scroll', handleScroll, { passive: true });
      }

      // Keep polling as backup
      const pollInterval = setInterval(handleScroll, 100);

      return () => {
        document.body.removeEventListener('scroll', handleScroll);
        document.documentElement.removeEventListener('scroll', handleScroll);
        window.removeEventListener('scroll', handleScroll);
        clearInterval(pollInterval);
      };
    };

    const cleanup = addListeners();

    // Initial call
    handleScroll();

    return cleanup;
  }, [scrollInfo.isScrollable, thresholds, debug, pagina]);

  if (debug) {
    return (
      <div
        style={{
          backgroundColor: 'lightBlue',
          position: 'absolute',
          zIndex: 10,
          borderRadius: 12,
          padding: 12,
        }}
      >
        <div>Universal Scroll Tracker</div>
        <div>Source: {scrollSource}</div>
        <div>Scrollable: {scrollInfo.isScrollable ? '✅' : '❌'}</div>
        <div>User Scrolled: {hasUserScrolled.current ? '✅' : '❌'}</div>
        <div>
          <div>ScrollTop: {scrollInfo.scrollTop}px</div>
          <div>Height: {scrollInfo.scrollHeight}px</div>
          <div>Client: {scrollInfo.clientHeight}px</div>
          <div>Scrollable: {scrollInfo.scrollableHeight}px</div>
        </div>
        <div>
          Triggered:{' '}
          {Array.from(triggeredThresholds.current).join(', ') || 'None'}
        </div>
      </div>
    );
  }

  return null;
}
